const R = 287.05 // Specific gas constant for dry air in J/(kg·K)
const Rv = 461.495 // Specific gas constant for water vapor in J/(kg·K)
const G = 9.81 // Acceleration due to gravity in m/s²
const L = 0.0065 // Standard temperature lapse rate in K/m
const EARTH_RADIUS = 6371220 // Radius of earth in meters
const EARTH_ROTATION_RATE = .000072921159 // radians per second
const clamp = (value, min, max) => Math.max(min, Math.min(max, value))
const multiplyMatrices = (a, b) => a.map((row, i) => row.map((_, j) => row.reduce((sum, _, k) => sum + a[i][k] * b[k][j], 0)));
const multiplyMatrixByScalar = (matrix, scalar) => matrix.map(row => row.map(value => value * scalar));
const addMatrices = (a, b) => a.map((row, i) => row.map((value, j) => value + b[i][j]));
const kToC = x => x - 273.15;
const metersToInches = (x) => x * 39.3700787401575;
const kgToLb = (x) => x * 2.20462262184878;

export function trajectory (params) {
  const scopeHeight = params.scopeHeight || 0
  const y0 = -scopeHeight

  if (params.Cd && params.Cd.length && params.G7) {
    throw new Error('Cannot specify both Cd and G7')
  }
  if (!params.Cd && params.G7) {
    const i7 = kgToLb(params.mass) / (metersToInches(params.diameter) ** 2 * params.G7)
    params.Cd = dragCurve(i7)
  }

  const aeros = [ 'Cd', 'Cda2', 'Cla', 'Cla2', 'Clp', 'Cma', 'Cma2', 'CmqCma', 'CmqCma2', 'Cnpa', 'Cnpa2', 'Cypa', 'Cypa2', 'Cnq', 'Cnq2' ]
  aeros.forEach(aero => {
    if (!params[aero]) {
      params[aero] = [[0, 0]]
    }
  })

  if (params.twistRate) {
    params.calibersPerTurn = params.twistRate / metersToInches(params.diameter)
  }

  const winds = params.winds.map(wind => {
    if (wind.direction !== undefined && wind.speed !== undefined) {
      return {
        x: wind.speed * Math.cos(wind.direction + Math.PI),
        y: 0,
        z: wind.speed * Math.sin(wind.direction + Math.PI),
        end: wind.end
      }
    } else {
      throw new Error('Wind must be in {speed, direction} format, got ' + JSON.stringify(wind))
    }
    return wind
  })

  let zeroAngle = 0
  if (params.zeroRange > 0) {
    let zeroResult = new SixDofSim({
      ...params,
      winds: [{x: 0, y: 0, z: 0}],
      phi0: 0,
      theta0: 0,
      pitchRate0: 0,
      yawRate0: 0,
      alpha0: 0,
      beta0: 0,
      x0: 0,
      y0,
      z0: 0
    }).integrate({
      maxRange: params.zeroRange,
      resultInterval: .01,
      maxTime: 30
    })

    const zero = zeroResult[zeroResult.length - 1]
    zeroAngle = -Math.atan2(zero.posY, zero.posX)
  }

  let dropAngle = 0
  let driftAngle = 0
  let error = Infinity
  let result
  const incline = params.incline

  //for (let i = 0; i < 10 && (error * 1000) > .1; ++i) {
  for (let i = 0; i < 10 && (error * 1000) > .1; ++i) {
    let simulation = new SixDofSim({
      ...params,
      winds,
      phi0: zeroAngle + dropAngle + incline,
      theta0: driftAngle,
      pitchRate0: 0,
      yawRate0: 0,
      alpha0: 0,
      beta0: 0,
      x0: 0,
      y0,
      z0: 0
    }).integrate({
      resultInterval: params.resultInterval,
      maxRange: params.range,
      maxTime: 30,
    })

    const lastResult = simulation[simulation.length - 1]
    const resultDropAngle = incline - Math.atan2(lastResult.posY, lastResult.posX)
    const resultDriftAngle = -Math.atan2(lastResult.posZ, lastResult.posX)
    dropAngle += resultDropAngle
    driftAngle += resultDriftAngle
    const dropError = resultDropAngle
    const driftError = resultDriftAngle
    error = Math.max(Math.abs(dropError), Math.abs(driftError))
    result = simulation
    if (params.debug) {
      console.log([
        `i=${i}`,
        `drop mils=${(dropAngle * 1000).toFixed(2)}`,
        `drift mils=${(driftAngle * 1000).toFixed(2)}`,
        `drop=${lastResult.posY.toFixed(2)}`,
        `drop error=${(resultDropAngle * 1000).toFixed(2)}mils`,
        `drift error=${(resultDriftAngle * 1000).toFixed(2)}mils`
      ].join('\t'))
    }
  }
  return {
    trajectory: result,
    dropMils: -dropAngle * 1000,
    driftMils: -driftAngle * 1000
  }
}

export default class SixDofSim {
  constructor(params) {
    this.validate(params)
    const {
      mass,
      Ix, Iy,
      Cd, Cda2,
      Cla, Cla2,
      Clp,
      Cma, Cma2,
      CmqCma, CmqCma2,
      Cnpa, Cnpa2,
      Cypa, Cypa2,
      Cnq, Cnq2,
      diameter,
      pressure, temperature,
      latitude, altitude, azimuth,
      winds,
      calibersPerTurn,
      v0,
      phi0, theta0,
      incline,
      pitchRate0, yawRate0,
      alpha0, beta0,
      x0, y0, z0,
      humidity
    } = params
    this.mass = mass
    this.Ix = Ix
    this.Iy = Iy
    this.Cd = Cd || [[0, 0]]
    this.Cda2 = Cda2 || [[0, 0]]
    this.Clp = Clp || [[0, 0]]
    this.Cla = Cla || [[0, 0]]
    this.Cla2 = Cla2 || [[0, 0]]
    this.Cma = Cma || [[0, 0]]
    this.Cma2 = Cma2 || [[0, 0]]
    this.CmqCma = CmqCma || [[0, 0]]
    this.CmqCma2 = CmqCma2 || [[0, 0]]
    this.Cnpa = Cnpa || [[0, 0]]
    this.Cnpa2 = Cnpa2 || [[0, 0]]
    this.Cnq = Cnq || [[0, 0]]
    this.Cnq2 = Cnq2 || [[0, 0]]
    this.Cypa = Cypa || [[0, 0]]
    this.Cypa2 = Cypa2 || [[0, 0]]
    this.diameter = diameter
    this.area = (Math.PI * (diameter / 2) ** 2) // m²
    this.pressure = pressure
    this.T0 = temperature // K
    this.latitude = parseFloat(latitude) || 0
    this.azimuth = parseFloat(azimuth) || 0
    this.altitude = parseFloat(altitude) || 0
    this.humidity = humidity // Relative humidity as percentage (0-1)
    this.rho0 = this.calcRho(this.T0, this.pressure, this.humidity) // kg/m³
    this.winds = winds || [{ x: 0, y: 0, z: 0 }]
    this.maxOrdinate = 0
    this.phi0 = phi0
    this.incline = incline || 0
    this.states = []
    const spinRate = 2 * Math.PI * v0 / (diameter * calibersPerTurn)
    const Q = Math.sin((theta0 + beta0)) ** 2 + Math.cos((theta0 + beta0)) ** 2 * Math.cos((phi0 + alpha0)) ** 2
    const unitXRate = (-yawRate0 * Math.cos((theta0 + beta0)) ** 2 * Math.sin((phi0 + alpha0)) * Math.cos((phi0 + alpha0)) + pitchRate0 * Math.sin((theta0 + beta0))) / Math.sqrt(Q)
    const unitYRate = (yawRate0 * Math.cos((theta0 + beta0)) ** 2 * Math.cos((phi0 + alpha0)) ** 2 + yawRate0 * Math.sin((theta0 + beta0)) ** 2) / Math.sqrt(Q)
    const unitZRate = (-yawRate0 * Math.sin((theta0 + beta0)) * Math.cos((theta0 + beta0)) * Math.sin((phi0 + alpha0)) - pitchRate0 * Math.cos((theta0 + beta0)) * Math.cos((phi0 + alpha0))) / Math.sqrt(Q)
    const unitX = Math.cos((phi0 + alpha0)) * Math.cos((theta0 + beta0))
    const unitY = Math.sin((phi0 + alpha0)) * Math.cos((theta0 + beta0))
    const unitZ = Math.sin((theta0 + beta0))
    const vx = v0 * Math.cos((phi0)) * Math.cos((theta0))
    const vy = v0 * Math.sin((phi0)) * Math.cos((theta0))
    const vz = v0 * Math.sin((theta0))
    const rollRate  = ((Ix * spinRate) / Iy) * unitX   + unitY * unitZRate   - unitZ * unitYRate
    const pitchRate = ((Ix * spinRate) / Iy) * unitY   - unitX * unitZRate   + unitZ * unitXRate
    const yawRate   = ((Ix * spinRate) / Iy) * unitZ   + unitX * unitYRate   - unitY * unitXRate
    const posX = x0
    const posY = y0
    const posZ = z0
    //console.log('initial state', { vx, vy, vz, rollRate, pitchRate, yawRate, unitX, unitY, unitZ, posX, posY, posZ })
    //console.log(`v(${vx.toFixed(1)}, ${vy.toFixed(1)}, ${vz.toFixed(1)})`)
    //console.log(`u(${unitX.toFixed(6)}, ${unitY.toFixed(6)}, ${unitZ.toFixed(6)})`)
    //console.log(`h(${rollRate.toFixed(6)}, ${pitchRate.toFixed(6)}, ${yawRate.toFixed(6)}`)
    this.state = [
      vx, vy, vz,
      posX, posY, posZ,
      rollRate, pitchRate, yawRate,
      unitX, unitY, unitZ
    ]
  }

  validate (params) {
    const {
      mass,
      Ix, Iy,
      diameter,
      pressure, temperature,
      latitude, altitude, azimuth,
      winds,
      calibersPerTurn,
      v0,
      phi0, theta0,
      pitchRate0, yawRate0,
      alpha0, beta0,
      x0, y0, z0,
      humidity,
      incline
    } = params
    const keys = [
      'alpha0', 'altitude', 'azimuth', 'beta0', 'calibersPerTurn',
      'diameter', 'humidity', 'incline', 'Ix', 'Iy',
      'latitude', 'mass', 'phi0', 'pitchRate0', 'pressure',
      'temperature', 'theta0', 'v0', 'winds', 'x0',
      'y0', 'yawRate0', 'z0', 'zeroRange', 'range', 'resultInterval',
      'twistRate', 'scopeHeight', 'G7', 'debug'
    ]
    const aeros = [
      'Cd', 'Cda2', 'Cla', 'Cla2', 'Clp', 'Cma', 'Cma2', 'CmqCma', 'CmqCma2',
      'Cnpa', 'Cnpa2', 'Cypa', 'Cypa2', 'Cnq', 'Cnq2'
    ]
    for (const key in params) {
      if (!keys.includes(key) && !aeros.includes(key)) {
        throw new Error(`Unknown parameter: ${key}`)
      }
    }
    if (!mass || mass <= 0) throw new Error(`mass must be > 0, got ${JSON.stringify(mass)}`)
    if (!Ix || Ix <= 0) throw new Error(`Ix must be > 0, got ${JSON.stringify(Ix)}`)
    if (!Iy || Iy <= 0) throw new Error(`Iy must be > 0, got ${JSON.stringify(Iy)}`)
    if (!diameter || diameter <= 0) throw new Error(`diameter must be > 0, got ${JSON.stringify(diameter)}`)
    if (typeof pressure !== 'number' || pressure < 0) throw new Error(`pressure must be >= 0, got ${JSON.stringify(pressure)}`)
    if (!temperature || temperature <= 0) throw new Error(`temperature must be > 0, got ${JSON.stringify(temperature)}`)
    if (!v0 || v0 <= 0) throw new Error(`v0 must be > 0, got ${JSON.stringify(v0)}`)
    if (typeof calibersPerTurn !== 'number' || calibersPerTurn <= 0) throw new Error(`calibersPerTurn must be > 0, got ${JSON.stringify(calibersPerTurn)}`)
    if (typeof x0 !== 'number') throw new Error(`x0 must be a number, got ${JSON.stringify(x0)}`)
    if (typeof y0 !== 'number') throw new Error(`y0 must be a number, got ${JSON.stringify(x0)}`)
    if (typeof z0 !== 'number') throw new Error(`z0 must be a number, got ${JSON.stringify(x0)}`)
    if (typeof latitude !== 'number' || latitude < -Math.PI/2 || latitude > Math.PI/2) throw new Error(`latitude must be between -pi/2 and pi/2 degrees, got ${JSON.stringify(latitude)}`)
    if (typeof azimuth !== 'number' || azimuth < 0 || azimuth >= 2 * Math.PI) throw new Error(`azimuth must be between 0 and 2pi degrees, got ${JSON.stringify(azimuth)}`)
    if (typeof altitude !== 'number') throw new Error(`altitude must be a number, got ${JSON.stringify(altitude)}`)
    if (typeof phi0 !== 'number') throw new Error(`phi0 must be a number, got ${JSON.stringify(phi0)}`)
    if (typeof theta0 !== 'number') throw new Error(`theta0 must be a number, got ${JSON.stringify(theta0)}`)
    if (typeof pitchRate0 !== 'number') throw new Error(`pitchRate0 must be a number, got ${JSON.stringify(pitchRate0)}`)
    if (typeof yawRate0 !== 'number') throw new Error(`yawRate0 must be a number, got ${JSON.stringify(yawRate0)}`)
    if (typeof alpha0 !== 'number') throw new Error(`alpha0 must be a number, got ${JSON.stringify(alpha0)}`)
    if (typeof beta0 !== 'number') throw new Error(`beta0 must be a number, got ${JSON.stringify(beta0)}`)
    if (typeof humidity !== 'number' || humidity < 0 || humidity > 1) throw new Error(`humidity must be a number between 0 and 1, got ${JSON.stringify(humidity)}`)
    if (typeof incline !== 'number' || incline > Math.PI || incline < -Math.PI) throw new Error(`incline must be a number between -PI and PI, got ${JSON.stringify(incline)}`)
    aeros.forEach(aero => {
      if (!params[aero] || !Array.isArray(params[aero]) || params[aero].length === 0) {
        throw new Error(`${aero} must be a non-empty array of [mach, value] pairs, got ${JSON.stringify(params[aero])}`)
      }
      params[aero].forEach(pair => {
        if (!Array.isArray(pair) || pair.length !== 2) {
          throw new Error(`${aero} must be an array of [mach, value] pairs, got ${JSON.stringify(pair)}`)
        }
        const [ mach, value ] = pair
        if (typeof mach !== 'number' || mach < 0) {
          throw new Error(`${aero} mach value must be a number >= 0, got ${JSON.stringify(mach)}`)
        }
        if (typeof value !== 'number') {
          throw new Error(`${aero} coefficient value must be a number, got ${JSON.stringify(value)}`)
        }
      })
      params[aero].sort((a, b) => a[0] - b[0])
    })
    if (!winds || !Array.isArray(winds) || winds.length === 0) throw new Error(`winds must be a non-empty array of {x, y, z, end} objects, got ${JSON.stringify(winds)}`)
    winds.forEach(wind => {
      if (typeof wind !== 'object') throw new Error(`each wind must be an object with {x, y, z, end} properties, got ${JSON.stringify(wind)}`)
      if (typeof wind.x !== 'number') throw new Error(`wind.x must be a number, got ${JSON.stringify(wind.x)}`)
      if (typeof wind.y !== 'number') throw new Error(`wind.y must be a number, got ${JSON.stringify(wind.y)}`)
      if (typeof wind.z !== 'number') throw new Error(`wind.z must be a number, got ${JSON.stringify(wind.z)}`)
      if (wind.end !== undefined && (typeof wind.end !== 'number' || wind.end < 0)) throw new Error(`wind.end must be a number >= 0 or undefined, got ${JSON.stringify(wind.end)}`)
    })
  }

  calcRho (temperature, pressure, humidity) {
    const celcius = kToC(temperature)
    const p1 = 6.1078 * Math.pow(10, (7.5 * celcius) / (celcius + 237.3))
    const pv = p1 * humidity * 100
    const pd = pressure - pv
    const rho = pd / (R * temperature) + pv / (Rv * temperature)
    return rho
  }

  interpolate (mach, coeffs) {
    if (mach <= coeffs[0][0]) return coeffs[0][1]
    if (mach >= coeffs[coeffs.length - 1][0]) return coeffs[coeffs.length-1][1]

    // Binary search for the interval
    let left = 0
    let right = coeffs.length - 1
    while (right - left > 1) {
      const mid = Math.floor((left + right) / 2)
      if (mach < coeffs[mid][0]) {
        right = mid
      } else {
        left = mid
      }
    }

    const ma1 = coeffs[left][0]
    const ma2 = coeffs[right][0]
    const cv1 = coeffs[left][1]
    const cv2 = coeffs[right][1]
    const v = cv1 + ((cv2 - cv1) * (mach - ma1)) / (ma2 - ma1)
    return v
  }

  posX () {
    return this.state[3]
  }

  posY () {
    return this.state[4]
  }

  range () {
    return Math.sqrt(this.state[3] ** 2 + this.state[4] ** 2 + this.state[5] ** 2)
  }

  mach (v) {
    const gamma = 1.4
    const T = this.T()
    const a = Math.sqrt(gamma * R * T)
    const machNumber = v / a
    return machNumber
  }

  gravity () {
    const posY = this.posY()
    if (this.latitude === 0) {
      return G
    }
    let y0 = this.altitude
    let y1 =  posY || 0
    const altitude = y0 + y1
    const g =
      9.8061977 * (1 - 0.0026 * Math.cos(2 * this.latitude))
      - 3.086e-6 * altitude
      + 7.2e-13 * altitude * altitude
    return g
  }

  T () {
    const posY = this.posY()
    const { T0 } = this
    const T = T0 - L * posY;
    return T;
  }

  rho () {
    const { rho0, T0 } = this
    const T = this.T();
    const alpha = G / (R * L) - 1;
    const rho = rho0 * Math.pow(T / T0, alpha);
    return rho
  }

  getSg (spinRate, Cma, v, rho) {
    const { Ix, Iy, diameter } = this
    const numerator = 2 * Math.pow(Ix, 2) * Math.pow(spinRate, 2);
    const denominator = Math.PI * rho * Iy * Cma * Math.pow(diameter, 3) * Math.pow(v, 2);
    const Sg = numerator / denominator;
    return Sg;
  }

  wind () {
    const { winds } = this
    const posX = this.posX()
    const currentWind = winds.find(w => !w.end || posX <= w.end)
    return currentWind
  }

  getSd (Cla, Cd, Cnpa, Cma, Cmq) {
    const { Ix, Iy, diameter, mass } = this
    const k1 = (mass * diameter ** 2) / Ix
    const k2 = (mass * diameter ** 2) / Iy
    const Sd = (2 * (Cla + k1 * Cnpa)) / (Cla - Cd - k2 * (Cmq + Cma))
    return Sd
  }

  alphaBeta () {
    const { Vx, Vy, Vz, unitX, unitY, unitZ } = this.getVariables(this.state)
    const v = [ Vx, Vy, Vz ]
    const u = [ unitX, unitY, unitZ ]
    const vMag = Math.sqrt(v[0]**2 + v[1]**2 + v[2]**2);
    const vNorm = [v[0] / vMag, v[1] / vMag, v[2] / vMag];

    const uMag = Math.sqrt(u[0]**2 + u[1]**2 + u[2]**2);
    const uNorm = [u[0] / uMag, u[1] / uMag, u[2] / uMag];

    const cross = [
      vNorm[1] * uNorm[2] - vNorm[2] * uNorm[1],
      vNorm[2] * uNorm[0] - vNorm[0] * uNorm[2],
      vNorm[0] * uNorm[1] - vNorm[1] * uNorm[0]
    ];

    const dot = vNorm[0] * uNorm[0] + vNorm[1] * uNorm[1] + vNorm[2] * uNorm[2];

    // Calculate the skew-symmetric matrix of the cross product
    const crossMatrix = [
      [0, -cross[2], cross[1]],
      [cross[2], 0, -cross[0]],
      [-cross[1], cross[0], 0]
    ];

    const identity = [
      [1, 0, 0],
      [0, 1, 0],
      [0, 0, 1]
    ];

    const crossNorm = Math.sqrt(cross[0]**2 + cross[1]**2 + cross[2]**2);
    const crossMatrixSquared = multiplyMatrices(crossMatrix, crossMatrix);
    const factor = (1 - dot) / (crossNorm**2);

    const rotationMatrix = addMatrices(
      addMatrices(identity, crossMatrix),
      multiplyMatrixByScalar(crossMatrixSquared, factor)
    );

    // Extract Euler angles from the rotation matrix
    const pitch = Math.atan2(rotationMatrix[1][0], rotationMatrix[0][0]);
    const yaw = Math.asin(rotationMatrix[2][0]);

    return [
      pitch,
      yaw
    ]
  }

  getVariables (state) {
    const { Ix, Iy } = this
    const [ Vx, Vy, Vz, posX, posY, posZ, rollRate, pitchRate, yawRate, unitX, unitY, unitZ ] = state
    const wind = this.wind()
    const vx = Vx - wind.x
    const vy = Vy - wind.y
    const vz = Vz - wind.z
    const v = Math.sqrt(vx ** 2 + vy ** 2 + vz ** 2)
    const V = Math.sqrt(Vx ** 2 + Vy ** 2 + Vz ** 2)
    const rho = this.rho()
    const g = this.gravity()
    const mach = this.mach(v)
    const alphaT = Math.acos(clamp((vx * unitX + vy * unitY + vz * unitZ) / v, -1, 1))
    const hdotx = rollRate * unitX + pitchRate * unitY + yawRate * unitZ
    const xdotx = unitX * unitX + unitY * unitY + unitZ * unitZ
    const spinRate = (Iy / Ix) * hdotx
    if (Math.abs(1 - xdotx) > .00001 && !this.notNormalized) {
      this.notNormalized = true
      console.log('unit vector not normalized, dt too large', { unitX, unitY, unitZ })
    }
    return {
      Vx, Vy, Vz,
      vx, vy, vz,
      rollRate, pitchRate, yawRate,
      unitX, unitY, unitZ,
      posX, posY, posZ,
      V, v,
      rho, g, mach, alphaT,
      hdotx, spinRate
    }
  }

  equationsOfMotion (state) {
    const {
      Vx, Vy, Vz,
      vx, vy, vz,
      rollRate, pitchRate, yawRate,
      unitX, unitY, unitZ,
      posX, posY,
      v,
      rho, g, mach, alphaT,
      hdotx, spinRate
    } = this.getVariables(state)

    const {
      diameter, mass, area, Iy, azimuth, latitude,
      Cd, Cda2, Cla, Cla2, Clp, Cma, Cma2, CmqCma, CmqCma2, Cnpa, Cnpa2, Cnq, Cnq2, Cypa, Cypa2
    } = this

    const cd0 = this.interpolate(mach, Cd)
    const cda2 = this.interpolate(mach, Cda2)
    const cla0 = this.interpolate(mach, Cla)
    const cla2 = this.interpolate(mach, Cla2)
    const clp = this.interpolate(mach, Clp)
    const cma0 = this.interpolate(mach, Cma)
    const cma2 = this.interpolate(mach, Cma2)
    const cmq0 = this.interpolate(mach, CmqCma)
    const cmq2 = this.interpolate(mach, CmqCma2)
    const cnpa0 = this.interpolate(mach, Cnpa)
    const cnpa2 = this.interpolate(mach, Cnpa2)
    const cnq0 = this.interpolate(mach, Cnq)
    const cnq2 = this.interpolate(mach, Cnq2)
    const cypa0 = this.interpolate(mach, Cypa)
    const cypa2 = this.interpolate(mach, Cypa2)

    const cd = cd0 + cda2 * (Math.sin(alphaT) ** 2)
    const cla = cla0 + cla2 * (Math.sin(alphaT) ** 2)
    const cma = cma0 + cma2 * (Math.sin(alphaT) ** 2)
    const cnpa = cnpa0 + cnpa2 * (Math.sin(alphaT) ** 2)
    const cnq = cnq0 + cnq2 * (Math.sin(alphaT) ** 2)
    const cypa = cypa0 + cypa2 * (Math.sin(alphaT) ** 2)
    const cmq = cmq0 + cmq2 * (Math.sin(alphaT) ** 2)

    const Ĉla  = (cla  * rho *     area                           ) / (2 * mass)
    const Ĉd   = (cd   * rho * v * area                           ) / (2 * mass)
    const Ĉma  = (cma  * rho * v * area * diameter                ) / (2 * Iy)
    const Ĉmq  = (cmq  * rho * v * area * diameter ** 2           ) / (2 * Iy)
    const Ĉlp  = (clp  * rho * v * area * diameter ** 2 * spinRate) / (2 * Iy)
    const Ĉnpa = (cnpa * rho *     area * diameter ** 2 * spinRate) / (2 * Iy)
    const ĈYpa = (cypa * rho *     area * diameter      * spinRate) / (2 * mass)
    const ĈNq  = (cnq  * rho * v * area * diameter                ) / (2 * mass)

    const gx = -g * posX / EARTH_RADIUS
    const gy = -g * (1 - 2 * posY / EARTH_RADIUS)
    const gz = 0

    const Λx = 2 * EARTH_ROTATION_RATE * (-Vy * Math.cos(latitude) * Math.sin(azimuth) - Vz * Math.sin(latitude))
    const Λy = 2 * EARTH_ROTATION_RATE * (Vx * Math.cos(latitude) * Math.sin(azimuth) + Vz * Math.cos(latitude) * Math.sin(azimuth))
    const Λz = 2 * EARTH_ROTATION_RATE * (Vx * Math.sin(latitude) - Vy * Math.cos(latitude) * Math.cos(azimuth))

    const Sg = this.getSg(spinRate, cma, v, rho)
    const Sd = this.getSd(cla, cd, cnpa, cma, cmq)

    const minSg = 1 / (Sd * (2 - Sd))
    const stable = Sd > 0 && Sd < 2 && Sg >= minSg && Sg > 1

    this.coeffs = { cd, cla, clp, cma, cmq, cnpa, cnq, cypa }
    this.Sg = Sg
    this.Sd = Sd
    this.stable = stable
    this.spinRate = spinRate

    return [
      -Ĉd * vx + Ĉla * (v ** 2 * unitX - v * vx * Math.cos(alphaT)) - ĈYpa * (unitY * vz - unitZ * vy) + ĈNq * (pitchRate * unitZ - yawRate * unitY) + gx + Λx,
      -Ĉd * vy + Ĉla * (v ** 2 * unitY - v * vy * Math.cos(alphaT)) - ĈYpa * (unitZ * vx - unitX * vz) + ĈNq * (yawRate * unitX - rollRate * unitZ) + gy + Λy,
      -Ĉd * vz + Ĉla * (v ** 2 * unitZ - v * vz * Math.cos(alphaT)) - ĈYpa * (unitX * vy - unitY * vx) + ĈNq * (rollRate * unitY - pitchRate * unitX) + gz + Λz,
      Vx,
      Vy + (Vx ** 2) / (2 * EARTH_RADIUS),
      Vz,
      Ĉlp * unitX + Ĉma * (vy * unitZ - vz * unitY) + Ĉnpa * (vx - v * unitX * Math.cos(alphaT)) + Ĉmq * (rollRate - hdotx * unitX),
      Ĉlp * unitY + Ĉma * (vz * unitX - vx * unitZ) + Ĉnpa * (vy - v * unitY * Math.cos(alphaT)) + Ĉmq * (pitchRate - hdotx * unitY),
      Ĉlp * unitZ + Ĉma * (vx * unitY - vy * unitX) + Ĉnpa * (vz - v * unitZ * Math.cos(alphaT)) + Ĉmq * (yawRate - hdotx * unitZ),
      pitchRate * unitZ - yawRate * unitY,
      yawRate * unitX - rollRate * unitZ,
      rollRate * unitY - pitchRate * unitX
    ]
  }

  // Runge-Kutta 4th order method
  rk4(t, dt) {
    let k1 = this.equationsOfMotion(this.state)
    let k2 = this.equationsOfMotion(this.state.map((v, i) => v + k1[i] * dt / 2))
    let k3 = this.equationsOfMotion(this.state.map((v, i) => v + k2[i] * dt / 2))
    let k4 = this.equationsOfMotion(this.state.map((v, i) => v + k3[i] * dt))

    let y = [ ...this.state ]
    let deltas = []
    for (let i = 0; i < y.length; i++) {
      const delta = (dt / 6) * (k1[i] + 2 * k2[i] + 2 * k3[i] + k4[i])
      deltas.push(delta)
    }
    for (let i = 0; i < y.length; i++) {
      y[i] += deltas[i]
    }
    return y
  }

  // Runge-Kutta-Fehlberg (RKF45) adaptive method
  rkf45(t, dt, tolerance) {
    const k1 = this.equationsOfMotion(this.state)
    const k2 = this.equationsOfMotion(this.state.map((v, i) => v + k1[i] * dt / 4))
    const k3 = this.equationsOfMotion(this.state.map((v, i) => v + k1[i] * dt * 3/32 + k2[i] * dt * 9/32))
    const k4 = this.equationsOfMotion(this.state.map((v, i) => v + k1[i] * dt * 1932/2197 - k2[i] * dt * 7200/2197 + k3[i] * dt * 7296/2197))
    const k5 = this.equationsOfMotion(this.state.map((v, i) => v + k1[i] * dt * 439/216 - k2[i] * dt * 8 + k3[i] * dt * 3680/513 - k4[i] * dt * 845/4104))
    const k6 = this.equationsOfMotion(this.state.map((v, i) => v - k1[i] * dt * 8/27 + k2[i] * dt * 2 - k3[i] * dt * 3544/2565 + k4[i] * dt * 1859/4104 - k5[i] * dt * 11/40))

    // 4th order solution
    const y4 = [...this.state]
    for (let i = 0; i < y4.length; i++) {
      y4[i] += dt * (25/216 * k1[i] + 1408/2565 * k3[i] + 2197/4104 * k4[i] - k5[i] / 5)
    }

    // 5th order solution
    const y5 = [...this.state]
    for (let i = 0; i < y5.length; i++) {
      y5[i] += dt * (16/135 * k1[i] + 6656/12825 * k3[i] + 28561/56430 * k4[i] - 9/50 * k5[i] + 2/55 * k6[i])
    }

    // Error estimation
    let error = 0
    for (let i = 0; i < y4.length; i++) {
      const localError = Math.abs(y5[i] - y4[i])
      error = Math.max(error, localError)
    }

    // Step size control
    const errorRatio = error / (tolerance * dt)
    const newDt = dt * Math.pow(0.9 / errorRatio, 0.2)
    const clampedDt = Math.max(dt * 0.1, Math.min(dt * 5, newDt))
    const minDt = 0.0000005

    return {
      state: y5,
      error: error,
      newDt: Math.max(clampedDt, minDt), // prevent dt from going to zero
      accepted: error <= tolerance || newDt < minDt // if newDt is very small, accept the step to avoid infinite loop
    }
  }

  integrate ({
    maxRange,
    resultInterval,
    method = 'rk4',
    tolerance = 1e-9,
    dt = 0.0001,
    maxTime = 10000,
  }) {
    if (!resultInterval) resultInterval = maxTime
    let time = 0
    let results = []
    let lastRange = 0
    let lastTimeLogged = 0
    let currentDt = dt

    while (time <= maxTime && (!maxRange || lastRange < maxRange)) {
      if (method === 'rkf45') {
        const rkfResult = this.rkf45(time, currentDt, tolerance)
        if (rkfResult.accepted) {
          this.state = rkfResult.state
          currentDt = rkfResult.newDt
          time += currentDt
        } else {
          currentDt = rkfResult.newDt // dt too large
          continue
        }
      } else if (method === 'euler') {
        const derivatives = this.equationsOfMotion(this.state)
        this.state = this.state.map((v, i) => v + derivatives[i] * dt)
        time += dt
      } else if (method === 'rk4') {
        this.state = this.rk4(time, currentDt)
        time += currentDt
      } else {
        throw new Error(`Unknown integration method: ${method}`)
      }

      const range = this.range()
      const posY = this.posY()
      this.maxOrdinate = Math.max(this.maxOrdinate, posY)
      if (isNaN(this.state[0]) || isNaN(this.state[1]) || isNaN(this.state[2])) {
        console.log('NaN detected')
        break
      }
      const [ alpha, beta ] = this.alphaBeta()
      if (time - lastTimeLogged > resultInterval || range > maxRange) {
        lastRange = range
        lastTimeLogged = time
        const result = this.getVariables(this.state)
        const losX = Math.sqrt(result.posX ** 2 + result.posY ** 2)
        const losY = result.posY - Math.sin(this.incline) * losX
        const energy = 0.5 * this.mass * result.vx ** 2
        results.push({
          ...result,
          ...this.coeffs,
          time,
          alpha, beta,
          losX, losY,
          energy,
          Sg: this.Sg,
          Sd: this.Sd,
          spinRate: this.spinRate,
          stable: this.stable ? 1 : 0
        })
      }
    }
    return results
  }

}


function dragCurve (i7) {
  const G7 = [
    [0.0, 0.1198], [0.05, 0.1197], [0.1, 0.1196], [0.15, 0.1194], [0.2, 0.1193], [0.25, 0.1194], [0.3, 0.1194], [0.35, 0.1194], [0.4, 0.1193], [0.45, 0.1193],
    [0.5, 0.1194], [0.55, 0.1193], [0.6, 0.1194], [0.65, 0.1197], [0.7, 0.1202], [0.725, 0.1207], [0.75, 0.1215], [0.775, 0.1226], [0.8, 0.1242], [0.825, 0.1266],
    [0.85, 0.1306], [0.875, 0.1368], [0.9, 0.1464], [0.925, 0.166], [0.95, 0.2054], [0.975, 0.2993], [1.0, 0.3803], [1.025, 0.4015], [1.05, 0.4043], [1.075, 0.4034],
    [1.1, 0.4014], [1.125, 0.3987], [1.15, 0.3955], [1.2, 0.3884], [1.25, 0.381], [1.3, 0.3732], [1.35, 0.3657], [1.4, 0.358], [1.5, 0.344], [1.55, 0.3376],
    [1.6, 0.3315], [1.65, 0.326], [1.7, 0.3209], [1.75, 0.316], [1.8, 0.3117], [1.85, 0.3078], [1.9, 0.3042], [1.95, 0.301], [2.0, 0.298], [2.05, 0.2951],
    [2.1, 0.2922], [2.15, 0.2892], [2.2, 0.2864], [2.25, 0.2835], [2.3, 0.2807], [2.35, 0.2779], [2.4, 0.2752], [2.45, 0.2725], [2.5, 0.2697], [2.55, 0.267],
    [2.6, 0.2643], [2.65, 0.2615], [2.7, 0.2588], [2.75, 0.2561], [2.8, 0.2533], [2.85, 0.2506], [2.9, 0.2479], [2.95, 0.2451], [3.0, 0.2424], [3.1, 0.2368],
    [3.2, 0.2313], [3.3, 0.2258], [3.4, 0.2205], [3.5, 0.2154], [3.6, 0.2106], [3.7, 0.206], [3.8, 0.2017], [3.9, 0.1975], [4.0, 0.1935], [4.2, 0.1861],
    [4.4, 0.1793], [4.6, 0.173], [4.8, 0.1672], [5.0, 0.1618],
  ]
  return G7.map(p => [p[0], p[1] * i7])
}
